import * as i0 from '@angular/core';
import { EventEmitter, forwardRef, Component, Input, Output, NgModule } from '@angular/core';
import * as i2 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';

class DurationPickerComponent {
    constructor() {
        this.valueChange = new EventEmitter();
        this.customOutput = new EventEmitter();
        this._disabled = false;
        this.regex = /^[\+\-]?P(?!$)(\d+Y)?(\d+M)?(\d+W)?(\d+D)?(T(?=\d+[HMS])(\d+H)?(\d+M)?(\d+S)?)?$/;
        this._negative = false;
        this._years = 0;
        this._months = 0;
        this._weeks = 0;
        this._days = 0;
        this._hours = 0;
        this._minutes = 0;
        this._seconds = 0;
        this.config = {
            showNegative: false,
            showButtons: true,
            showPreview: true,
            showLetters: true,
            showYears: true,
            showMonths: true,
            showWeeks: true,
            showDays: true,
            showHours: true,
            showMinutes: true,
            showSeconds: true,
            zeroValue: 'PT0S',
            previewFormat: 'ISO',
            customOutputFormat: 'ISO',
            labels: {
                years: 'Y',
                months: 'M',
                weeks: 'W',
                days: 'D',
                hours: 'H',
                minutes: 'M',
                seconds: 'S',
            },
            unitSteps: {
                years: 1,
                months: 1,
                weeks: 1,
                days: 1,
                hours: 1,
                minutes: 1,
                seconds: 1,
            },
        };
        this.onChange = (_) => { };
        this.onTouched = () => { };
    }
    set options(options) {
        this.attachChanges(this.validateOptions(options));
    }
    get value() {
        return this._value;
    }
    set value(value) {
        this._value = value;
        this.parse();
        this.emitCustomOutput();
    }
    get disabled() {
        return this._disabled;
    }
    set disabled(disabled) {
        this._disabled = disabled;
    }
    get preview() {
        if (!this.config.previewFormat || this.config.previewFormat.length === 0 || this.config.previewFormat === 'ISO') {
            return this.value;
        }
        return this.injectValuesIntoString(this.config.previewFormat);
    }
    get negative() { return this._negative; }
    set negative(value) {
        this._negative = value;
        this.emitNewValue();
    }
    get years() { return this._years; }
    set years(value) {
        value = this.parseNumber(value) > 0 ? value : 0;
        this._years = value;
        this.emitNewValue();
    }
    get months() { return this._months; }
    set months(value) {
        value = this.parseNumber(value) > 0 ? value : 0;
        this._months = value;
        this.emitNewValue();
    }
    get weeks() { return this._weeks; }
    set weeks(value) {
        value = this.parseNumber(value) > 0 ? value : 0;
        this._weeks = value;
        this.emitNewValue();
    }
    get days() { return this._days; }
    set days(value) {
        value = this.parseNumber(value) > 0 ? value : 0;
        this._days = value;
        this.emitNewValue();
    }
    get hours() { return this._hours; }
    set hours(value) {
        value = this.parseNumber(value) > 0 ? value : 0;
        this._hours = value;
        this.emitNewValue();
    }
    get minutes() { return this._minutes; }
    set minutes(value) {
        value = this.parseNumber(value) > 0 ? value : 0;
        this._minutes = value;
        this.emitNewValue();
    }
    get seconds() { return this._seconds; }
    set seconds(value) {
        value = this.parseNumber(value) > 0 ? value : 0;
        this._seconds = value;
        this.emitNewValue();
    }
    get valueInSeconds() {
        return (this.negative ? -1 : 1) * (this.seconds +
            this.minutes * 60 +
            this.hours * 3600 +
            this.days * 86400 +
            this.weeks * 6048000 +
            this.months * 25920000 +
            this.years * 315360000);
    }
    get valueInMilliseconds() {
        return this.valueInSeconds * 1000;
    }
    ngOnInit() {
        this.parse();
        this.value = this.generate();
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    writeValue(value) {
        if (value) {
            this.value = value;
        }
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    parse() {
        if (!this.value) {
            return;
        }
        const match = this.regex.exec(this.value);
        if (!match) {
            console.error(`DurationPicker: invalid initial value: ${this.value}`);
            return;
        }
        this._negative = match[0].startsWith('-');
        this._years = this.parseNumber(match[1]);
        this._months = this.parseNumber(match[2]);
        this._weeks = this.parseNumber(match[3]);
        this._days = this.parseNumber(match[4]);
        this._hours = this.parseNumber(match[6]);
        this._minutes = this.parseNumber(match[7]);
        this._seconds = this.parseNumber(match[8]);
    }
    parseNumber(value) {
        return value ? parseInt(value, 10) : 0;
    }
    generate() {
        let output = 'P';
        if (this.config.showNegative && this.negative) {
            output = '-' + output;
        }
        if (this.config.showYears && this.years) {
            output += `${this.years}Y`;
        }
        if (this.config.showMonths && this.months) {
            output += `${this.months}M`;
        }
        if (this.config.showWeeks && this.weeks) {
            output += `${this.weeks}W`;
        }
        if (this.config.showDays && this.days) {
            output += `${this.days}D`;
        }
        if ((this.config.showHours && this.hours)
            || (this.config.showMinutes && this.minutes)
            || (this.config.showSeconds && this.seconds)) {
            output += 'T';
            if (this.config.showHours && this.hours) {
                output += `${this.hours}H`;
            }
            if (this.config.showMinutes && this.minutes) {
                output += `${this.minutes}M`;
            }
            if (this.config.showSeconds && this.seconds) {
                output += `${this.seconds}S`;
            }
        }
        // if all values are empty, just output null
        if (output === 'P' || output === '-P') {
            output = this.config.zeroValue;
        }
        return output;
    }
    injectValuesIntoString(text) {
        return text
            .replace('{{Y}}', `${this.years}`)
            .replace('{{M}}', `${this.months}`)
            .replace('{{W}}', `${this.weeks}`)
            .replace('{{D}}', `${this.days}`)
            .replace('{{h}}', `${this.hours}`)
            .replace('{{m}}', `${this.minutes}`)
            .replace('{{s}}', `${this.seconds}`);
    }
    emitNewValue() {
        this.value = this.generate();
        this.valueChange.emit(this.value);
        this.emitCustomOutput();
        this.onTouched();
        this.onChange(this.value);
    }
    emitCustomOutput() {
        let customOutput = this.value;
        if (this.config.customOutputFormat && this.config.customOutputFormat.length > 0) {
            switch (this.config.customOutputFormat) {
                case 'ISO': break;
                case 'timestamp':
                    customOutput = this.valueInMilliseconds;
                    break;
                case 'seconds':
                    customOutput = this.valueInSeconds;
                    break;
                default: this.injectValuesIntoString(this.config.customOutputFormat);
            }
        }
        this.customOutput.emit(customOutput);
    }
    // Attach all the changes received in the options object
    attachChanges(options) {
        Object.keys(options).forEach(param => {
            if (this.config.hasOwnProperty(param)) {
                if (typeof (this.config)[param] === 'object') {
                    (this.config)[param] = Object.assign(Object.assign({}, (this.config)[param]), options[param]);
                }
                else {
                    (this.config)[param] = options[param];
                }
            }
        });
    }
    validateOptions(options) {
        const validatedOptions = Object.assign({}, options);
        if (typeof options.unitSteps === 'object' && Object.values(options.unitSteps).some((unitStep) => unitStep <= 0)) {
            console.warn('Use positive values for unit steps');
            validatedOptions.unitSteps = Object.keys(options.unitSteps).reduce((steps, unit) => {
                steps[unit] = options.unitSteps[unit] > 0 ? options.unitSteps[unit] : 1;
                return steps;
            }, {});
        }
        return validatedOptions;
    }
}
DurationPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.0", ngImport: i0, type: DurationPickerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DurationPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.1.0", type: DurationPickerComponent, selector: "ngx-duration-picker", inputs: { options: "options", value: "value", disabled: "disabled" }, outputs: { valueChange: "valueChange", customOutput: "customOutput" }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DurationPickerComponent),
            multi: true
        }
    ], ngImport: i0, template: "<table>\n  <tbody>\n\n  <tr *ngIf=\"config.showLetters\">\n    <td *ngIf=\"config.showNegative\"></td>\n    <td id=\"dp-years-label\" *ngIf=\"config.showYears\">{{ config.labels.years }}</td>\n    <td id=\"dp-months-label\" *ngIf=\"config.showMonths\">{{ config.labels.months }}</td>\n    <td id=\"dp-weeks-label\" *ngIf=\"config.showWeeks\">{{ config.labels.weeks }}</td>\n    <td id=\"dp-days-label\" *ngIf=\"config.showDays\">{{ config.labels.days }}</td>\n    <td id=\"dp-hours-label\" *ngIf=\"config.showHours\">{{ config.labels.hours }}</td>\n    <td id=\"dp-minutes-label\" *ngIf=\"config.showMinutes\">{{ config.labels.minutes }}</td>\n    <td id=\"dp-seconds-label\" *ngIf=\"config.showSeconds\">{{ config.labels.seconds }}</td>\n  </tr>\n\n  <tr *ngIf=\"config.showButtons\">\n    <td *ngIf=\"config.showNegative\"></td>\n\n    <td *ngIf=\"config.showYears\">\n      <a class=\"btn btn-link\" id=\"dp-years-up\" (click)=\"years = years + config.unitSteps.years\" [ngClass]=\"{ 'disabled': disabled }\">\n        <span class=\"bs-chevron bs-chevron-up\"></span>\n      </a>\n    </td>\n\n    <td *ngIf=\"config.showMonths\">\n      <a class=\"btn btn-link\" id=\"dp-months-up\" (click)=\"months = months + config.unitSteps.months\" [ngClass]=\"{ 'disabled': disabled }\">\n        <span class=\"bs-chevron bs-chevron-up\"></span>\n      </a>\n    </td>\n\n    <td *ngIf=\"config.showWeeks\">\n      <a class=\"btn btn-link\" id=\"dp-weeks-up\" (click)=\"weeks = weeks + config.unitSteps.weeks\" [ngClass]=\"{ 'disabled': disabled }\">\n        <span class=\"bs-chevron bs-chevron-up\"></span>\n      </a>\n    </td>\n\n    <td *ngIf=\"config.showDays\">\n      <a class=\"btn btn-link\" id=\"dp-days-up\" (click)=\"days = days + config.unitSteps.days\" [ngClass]=\"{ 'disabled': disabled }\">\n        <span class=\"bs-chevron bs-chevron-up\"></span>\n      </a>\n    </td>\n\n    <td *ngIf=\"config.showHours\">\n      <a class=\"btn btn-link\" id=\"dp-hours-up\" (click)=\"hours = hours + config.unitSteps.hours\" [ngClass]=\"{ 'disabled': disabled }\">\n        <span class=\"bs-chevron bs-chevron-up\"></span>\n      </a>\n    </td>\n\n    <td *ngIf=\"config.showMinutes\">\n      <a class=\"btn btn-link\" id=\"dp-minutes-up\" (click)=\"minutes = minutes + config.unitSteps.minutes\" [ngClass]=\"{ 'disabled': disabled }\">\n        <span class=\"bs-chevron bs-chevron-up\"></span>\n      </a>\n    </td>\n\n    <td *ngIf=\"config.showSeconds\">\n      <a class=\"btn btn-link\" id=\"dp-seconds-up\" (click)=\"seconds = seconds + config.unitSteps.seconds\" [ngClass]=\"{ 'disabled': disabled }\">\n        <span class=\"bs-chevron bs-chevron-up\"></span>\n      </a>\n    </td>\n  </tr>\n\n  <tr>\n    <td *ngIf=\"config.showNegative\">\n      <a\n        class=\"btn btn-link status-container\"\n        id=\"dp-sign\"\n        (click)=\"negative = !negative\"\n        [ngClass]=\"{ 'disabled': disabled }\"\n      >\n        <span *ngIf=\"!negative\" class=\"bs-icon bs-icon-plus\"></span>\n        <span *ngIf=\"negative\" class=\"bs-icon bs-icon-minus\"></span>\n      </a>\n    </td>\n\n    <td *ngIf=\"config.showYears\">\n      <input (blur)=\"onTouched()\" type=\"number\" placeholder=\"Y\" id=\"dp-years\" [(ngModel)]=\"years\" [disabled]=\"disabled\">\n    </td>\n\n    <td *ngIf=\"config.showMonths\">\n      <input (blur)=\"onTouched()\" type=\"number\" placeholder=\"M\" id=\"dp-months\" [(ngModel)]=\"months\" [disabled]=\"disabled\">\n    </td>\n\n    <td *ngIf=\"config.showWeeks\">\n      <input (blur)=\"onTouched()\" type=\"number\" placeholder=\"W\" id=\"dp-weeks\" [(ngModel)]=\"weeks\" [disabled]=\"disabled\">\n    </td>\n\n    <td *ngIf=\"config.showDays\">\n      <input (blur)=\"onTouched()\" type=\"number\" placeholder=\"D\" id=\"dp-days\" [(ngModel)]=\"days\" [disabled]=\"disabled\">\n    </td>\n\n    <td *ngIf=\"config.showHours\">\n      <input (blur)=\"onTouched()\" type=\"number\" placeholder=\"H\" id=\"dp-hours\" [(ngModel)]=\"hours\" [disabled]=\"disabled\">\n    </td>\n\n    <td *ngIf=\"config.showMinutes\">\n      <input (blur)=\"onTouched()\" type=\"number\" placeholder=\"M\" id=\"dp-minutes\" [(ngModel)]=\"minutes\" [disabled]=\"disabled\">\n    </td>\n\n    <td *ngIf=\"config.showSeconds\">\n      <input (blur)=\"onTouched()\" type=\"number\" placeholder=\"S\" id=\"dp-seconds\" [(ngModel)]=\"seconds\" [disabled]=\"disabled\">\n    </td>\n\n    <td *ngIf=\"config.showPreview\">\n      <span id=\"dp-duration-value\">{{ preview }}</span>\n    </td>\n  </tr>\n\n  <tr *ngIf=\"config.showButtons\">\n    <td *ngIf=\"config.showNegative\"></td>\n\n    <td *ngIf=\"config.showYears\">\n      <a class=\"btn btn-link\" id=\"dp-years-down\" (click)=\"years = years - config.unitSteps.years\" [ngClass]=\"{ 'disabled': disabled }\">\n        <span class=\"bs-chevron bs-chevron-down\"></span>\n      </a>\n    </td>\n\n    <td *ngIf=\"config.showMonths\">\n      <a class=\"btn btn-link\" id=\"dp-months-down\" (click)=\"months = months - config.unitSteps.months\" [ngClass]=\"{ 'disabled': disabled }\">\n        <span class=\"bs-chevron bs-chevron-down\"></span>\n      </a>\n    </td>\n\n    <td *ngIf=\"config.showWeeks\">\n      <a class=\"btn btn-link\" id=\"dp-weeks-down\" (click)=\"weeks = weeks - config.unitSteps.weeks\" [ngClass]=\"{ 'disabled': disabled }\">\n        <span class=\"bs-chevron bs-chevron-down\"></span>\n      </a>\n    </td>\n\n    <td *ngIf=\"config.showDays\">\n      <a class=\"btn btn-link\" id=\"dp-days-down\" (click)=\"days = days - config.unitSteps.days\" [ngClass]=\"{ 'disabled': disabled }\">\n        <span class=\"bs-chevron bs-chevron-down\"></span>\n      </a>\n    </td>\n\n    <td *ngIf=\"config.showHours\">\n      <a class=\"btn btn-link\" id=\"dp-hours-down\" (click)=\"hours = hours - config.unitSteps.hours\" [ngClass]=\"{ 'disabled': disabled }\">\n        <span class=\"bs-chevron bs-chevron-down\"></span>\n      </a>\n    </td>\n\n    <td *ngIf=\"config.showMinutes\">\n      <a class=\"btn btn-link\" id=\"dp-minutes-down\" (click)=\"minutes = minutes - config.unitSteps.minutes\" [ngClass]=\"{ 'disabled': disabled }\">\n        <span class=\"bs-chevron bs-chevron-down\"></span>\n      </a>\n    </td>\n\n    <td *ngIf=\"config.showSeconds\">\n      <a class=\"btn btn-link\" id=\"dp-seconds-down\" (click)=\"seconds = seconds - config.unitSteps.seconds\" [ngClass]=\"{ 'disabled': disabled }\">\n        <span class=\"bs-chevron bs-chevron-down\"></span>\n      </a>\n    </td>\n  </tr>\n\n  </tbody>\n</table>\n", styles: ["@charset \"UTF-8\";input,tr{text-align:center}input{width:30px;height:26px;margin-right:2px;margin-left:2px;padding:0;font-size:13px;color:#666;border:1px solid #e7e7e7;border-radius:4px;box-shadow:inset 0 1px 1px rgba(0,0,0,.075)}input[type=checkbox]{display:none}.status-container{margin-right:5px}a.btn{cursor:pointer;padding:1px}#dp-duration-value{margin-left:10px}input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}input[type=number]{-moz-appearance:textfield}input[disabled]{cursor:not-allowed;background-color:#eee;color:#9e9999}a.disabled{cursor:not-allowed;pointer-events:none}.bs-chevron{border-style:solid;display:block;width:9px;height:9px;position:relative;border-width:3px 0 0 3px}.bs-chevron-up{transform:rotate(45deg);top:2px}.bs-chevron-down{transform:rotate(-135deg);top:-2px}.bs-icon{position:relative;top:1px;display:inline-block;font-style:normal;font-weight:400;line-height:1;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale}.bs-icon-plus:before{content:\"+\"}.bs-icon-minus:before{content:\"\u2212\"}"], directives: [{ type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i2.NumberValueAccessor, selector: "input[type=number][formControlName],input[type=number][formControl],input[type=number][ngModel]" }, { type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.0", ngImport: i0, type: DurationPickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ngx-duration-picker',
                    templateUrl: './duration-picker.component.html',
                    styleUrls: ['./duration-picker.component.scss'],
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => DurationPickerComponent),
                            multi: true
                        }
                    ],
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { options: [{
                type: Input
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], customOutput: [{
                type: Output
            }], disabled: [{
                type: Input
            }] } });

class DurationPickerModule {
}
DurationPickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.0", ngImport: i0, type: DurationPickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DurationPickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.1.0", ngImport: i0, type: DurationPickerModule, declarations: [DurationPickerComponent], imports: [CommonModule,
        FormsModule], exports: [DurationPickerComponent] });
DurationPickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.1.0", ngImport: i0, type: DurationPickerModule, imports: [[
            CommonModule,
            FormsModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.0", ngImport: i0, type: DurationPickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                    ],
                    declarations: [
                        DurationPickerComponent,
                    ],
                    exports: [
                        DurationPickerComponent,
                    ],
                }]
        }] });

/*
 * Public API Surface of ngx-duration-picker
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DurationPickerComponent, DurationPickerModule };
